import {
  rectMinX,
  rectMinY,
  rectMaxX,
  rectMaxY,
  rectEdgeOpposite,
  rectScale,
  rectMidOfEdge,
  rectEdgeAxis,
} from 'utils/geometry'
import { roundUpTo } from 'utils/math'
import { Rect, RectEdge, Point, Axis } from 'modules/shares/types'

interface MeasurementAttributes {
  startRect: Rect
  endRect: Rect
  edge: RectEdge
}

export default class Measurement {
  readonly startRect: Rect
  readonly endRect: Rect
  readonly edge: RectEdge

  readonly startPoint: Point
  readonly endPoint: Point

  readonly length: number

  /**
   * Given an endRect, a startPoint and a measurement edge; calculate the
   * end point.
   *
   * Keep in mind that which edge of the end rectangle to put the measurement on
   * depends on if the rectangle intersects or is disjoint on that axis.
   *
   * For example measuring a disjoint layer to the right, startPoint.x is the
   * right side of the start rectangle (maxX):
   *
   * ┌ ─ ─     ┌────┐
   *      │    │    │
   * │     ───▶│    │
   *      │    │    │
   * └ ─ ─     └────┘
   *
   * The left side of the end rectangle (minX) is larger than the
   * start point (maxX). This means the calculation becomes:
   *
   * rectMinX(endRect) - rectMaxX(startRect)
   *
   *
   * When measuring intersecting layers, startPoint.x is still the right
   * side of the start rectangle (maxX):
   *
   * ┌ ─ ─ ─ ┐
   *      ┌────────┐
   * │    │──┼────▶│
   *      └────────┘
   * └ ─ ─ ─ ┘
   *
   *
   * In this case however, the left side of the end rectangle (minX) is smaller
   * than the start point. Which means we need to measure from the right side of
   * the start rectangle to the right side of the end rectangle. Making the
   * final calculation:
   *
   * rectMaxX(endRect) - rectMaxX(startRect)
   *
   * This is true for all possible measurement directions. This method exits to
   * abstract that complexity outside of the constructor.
   */
  private static calculateEndpoint(
    endRect: Rect,
    startPoint: Point,
    edge: RectEdge
  ): Point {
    let position: number

    switch (edge) {
      case 'minX':
        position =
          rectMaxX(endRect) < startPoint.x
            ? rectMaxX(endRect)
            : rectMinX(endRect)

        return { x: startPoint.x + (position - startPoint.x), y: startPoint.y }

      case 'maxX':
        position =
          rectMinX(endRect) > startPoint.x
            ? rectMinX(endRect)
            : rectMaxX(endRect)
        return { x: startPoint.x + (position - startPoint.x), y: startPoint.y }

      case 'minY':
        position =
          rectMaxY(endRect) < startPoint.y
            ? rectMaxY(endRect)
            : rectMinY(endRect)

        return { x: startPoint.x, y: startPoint.y + (position - startPoint.y) }

      case 'maxY':
        position =
          rectMinY(endRect) > startPoint.y
            ? rectMinY(endRect)
            : rectMaxY(endRect)
        return { x: startPoint.x, y: startPoint.y + (position - startPoint.y) }
    }
  }

  static calculateLength(
    startPoint: Point,
    endPoint: Point,
    edge: RectEdge
  ): number {
    switch (rectEdgeAxis(edge)) {
      case 'x':
        return parseFloat(roundUpTo(Math.abs(endPoint.x - startPoint.x), 2))

      case 'y':
        return parseFloat(roundUpTo(Math.abs(endPoint.y - startPoint.y), 2))
    }
  }

  constructor({ startRect, endRect, edge }: MeasurementAttributes) {
    this.startRect = startRect
    this.endRect = endRect
    this.edge = edge

    this.startPoint = rectMidOfEdge(startRect, edge)
    this.endPoint = Measurement.calculateEndpoint(
      endRect,
      this.startPoint,
      edge
    )

    this.length = Measurement.calculateLength(
      this.startPoint,
      this.endPoint,
      edge
    )
  }

  get axis(): Axis {
    return rectEdgeAxis(this.edge)
  }

  reversed(): Measurement {
    return new Measurement({
      startRect: this.endRect,
      endRect: this.startRect,
      edge: rectEdgeOpposite(this.edge),
    })
  }

  scaled(factor: number): Measurement {
    return new Measurement({
      startRect: rectScale(this.startRect, factor),
      endRect: rectScale(this.endRect, factor),
      edge: this.edge,
    })
  }
}
